import * as PIXI from "pixi.js";
import {IPoint, IRectangle} from "./Geom";

const tp = [
    new PIXI.Point(),
    new PIXI.Point(),
    new PIXI.Point(),
    new PIXI.Point(),
];
const tr = new PIXI.Rectangle();
const tm = [
    new PIXI.Matrix(),
    new PIXI.Matrix(),
];

export const ContainerExtensions = {
    get zIndex(): number {
        const that = this as any as PIXI.Container;
        if (that.parent) {
            return that.parent.getChildIndex(that);
        }
        return -1;
    },
    set zIndex(index: number) {
        const that = this as any as PIXI.Container;
        if (that.parent && that.parent.children && that.parent.children.length > 0) {
            const n = that.parent.children.length;
            if (index < 0) {
                index += n;
                if (index < 0) {
                    index = 0;
                }
            }
            if (index >= n) {
                index = n - 1;
            }
            that.parent.setChildIndex(that, index);
        }
    },
    removeFromParent(this: PIXI.Container): void {
        if (this.parent) {
            this.parent.removeChild(this);
        }
    },
    changeParent(this: PIXI.Container, parent: PIXI.Container): void {
        if (this.parent !== parent && parent) {
            if (!this.parent) {
                parent.addChild(this);
            } else {
                // updateTransform is not enough, this call updates worldTransform properly
                this.getBounds(false, tr);
                parent.getBounds(false, tr);
                let thisMatrix = tm[0];
                const parentMatrix = tm[1];
                this.worldTransform.copyFrom(thisMatrix);
                parent.worldTransform.copyFrom(parentMatrix);
                thisMatrix = thisMatrix.prepend(parentMatrix.invert());
                thisMatrix.decompose(this.transform);
                parent.addChild(this);
            }
        }
    },
    getChildren<T extends PIXI.DisplayObject>(this: PIXI.Container): T[] {
        return this.children as any;
    },
    destroyChildren(this: PIXI.Container): void {
        while (this.children.length > 0) {
            this.children[this.children.length - 1].destroy();
        }
    },
    getDescendants<T extends PIXI.DisplayObject>(this: PIXI.Container, predicate?: (it: PIXI.DisplayObject) => boolean): T[] {
        const children: T[] = [];
        for (const child of this.children) {
            if (!predicate || predicate(child)) {
                children.push(child as T);
                if (child instanceof PIXI.Container) {
                    children.push(...ContainerExtensions.getDescendants.call(child, predicate) as T[]);
                }
            }
        }
        return children;
    },
    alignBy(this: PIXI.Container, target: IRectangle, anchorX?: number | IPoint, anchorY?: number) {
        const r = this.getLocalBounds(tr);
        r.x *= this.scale.x;
        r.y *= this.scale.y;
        r.width *= this.scale.x;
        r.height *= this.scale.y;
        this.x = target.x - r.x + (target.width - r.width) * (typeof anchorX === "object" ? anchorX.x : anchorX || 0);
        this.y = target.y - r.y + (target.height - r.height) * (typeof anchorX === "object" ? anchorX.y : anchorY || 0);
    },
    get rootContainer() {
        let p = this as any as PIXI.Container;
        while (p.parent) {
            p = p.parent;
        }
        return p;
    },
    getGlobalBounds(this: PIXI.DisplayObject, root?: PIXI.DisplayObject, rect?: IRectangle) {
        if (!root) {
            root = this;
            while (root.parent) {
                root = root.parent;
            }
        }
        const bounds = rect || this.getLocalBounds(tr);
        let minX = Number.MAX_VALUE;
        let minY = Number.MAX_VALUE;
        let maxX = Number.MIN_VALUE;
        let maxY = Number.MIN_VALUE;
        const localPoint = tp[0];
        const globalPoint = tp[1];
        for (let i = 0; i < 2; i++) {
            for (let j = 0; j < 2; j++) {
                localPoint.x = bounds.x + i * bounds.width;
                localPoint.y = bounds.y + j * bounds.height;
                root.toLocal(localPoint, this, globalPoint, i > 0);
                minX = Math.min(minX, globalPoint.x);
                minY = Math.min(minY, globalPoint.y);
                maxX = Math.max(maxX, globalPoint.x);
                maxY = Math.max(maxY, globalPoint.y);
            }
        }
        bounds.x = minX;
        bounds.y = minY;
        bounds.width = maxX - minX;
        bounds.height = maxY - minY;
        return bounds;
    },
    getAncestor(this: PIXI.Container, condition: (it: PIXI.Container) => boolean): PIXI.Container | undefined {
        let p = this.parent;
        while (p && !condition(p)) {
            p = p.parent;
        }
        return p;
    },
};
